import { useState, useEffect } from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';

const severityColors = {
  critical: 'bg-red-600/20 text-red-400 border-red-500/30',
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
};

const severityLabels = {
  critical: { en: 'Critical', cs: 'Kritická' },
  high: { en: 'High', cs: 'Vysoká' },
  medium: { en: 'Medium', cs: 'Střední' },
  low: { en: 'Low', cs: 'Nízká' }
};

const causeIcons = {
  'slow-server': Icons.Server,
  'redirect-chain': Icons.ArrowRight,
  'render-blocking-css': Icons.Code,
  'render-blocking-js': Icons.FileJs,
  'third-party-blocking': Icons.Globe,
  'lazy-above-fold': Icons.Image,
  'large-image': Icons.Image,
  'slow-resource': Icons.Clock,
  'webfont-blocking': Icons.Type,
  'render-delay': Icons.Zap
};

const causeLabels = {
  'slow-server': { en: 'Slow Server Response', cs: 'Pomalá odpověď serveru' },
  'redirect-chain': { en: 'Redirect Chain', cs: 'Řetěz přesměrování' },
  'render-blocking-css': { en: 'Render-Blocking CSS', cs: 'CSS blokující render' },
  'render-blocking-js': { en: 'Render-Blocking JS', cs: 'JS blokující render' },
  'third-party-blocking': { en: 'Third-Party Blocking', cs: 'Blokování třetími stranami' },
  'lazy-above-fold': { en: 'Lazy Loading Above Fold', cs: 'Lazy loading nad foldem' },
  'large-image': { en: 'Large Image', cs: 'Velký obrázek' },
  'slow-resource': { en: 'Slow Resource Loading', cs: 'Pomalé načítání zdroje' },
  'webfont-blocking': { en: 'Blocking Web Fonts', cs: 'Blokující webfonty' },
  'render-delay': { en: 'Render Delay', cs: 'Zpoždění vykreslení' }
};

export const LCPDiagnosticsPanel = ({ url, user, onResultsChange }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [multiPageLoading, setMultiPageLoading] = useState(false);
  const [multiPageResults, setMultiPageResults] = useState(null);
  const [expandedPageIndex, setExpandedPageIndex] = useState(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [skipCmp, setSkipCmp] = useState(false);

  useEffect(() => {
    if (!onResultsChange) return;
    if (results) {
      onResultsChange({ deepLCP: { ...results, screenshot, skipCmp } });
    } else {
      onResultsChange({ deepLCP: null });
    }
  }, [results, screenshot, skipCmp]);
  
  const hasDeepAccess = user?.tier === 'premium' || user?.tier === 'enterprise' || user?.is_admin;

  const runDiagnostics = async () => {
    if (!url || !user) return;
    
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/lab-audits/lcp-diagnostics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url, skipCmp })
      });
      
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        return;
      }
      if (data.success && data.data) {
        setResults(data.data);
      } else {
        setError(data.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('LCP diagnostics error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runMultiPageLCP = async () => {
    if (!url || !user) return;
    
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    setMultiPageLoading(true);
    try {
      const normalizedUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      const response = await fetch('/api/lab-audits/multi-page-lcp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: normalizedUrl, maxPages: 10 })
      });
      
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        return;
      }
      if (data.success && data.data) {
        setMultiPageResults(data.data);
        onResultsChange?.({ multiPageLCP: data.data });
      }
    } catch (err) {
      console.error('Multi-page LCP error:', err);
    } finally {
      setMultiPageLoading(false);
    }
  };

  const captureScreenshot = async () => {
    if (!url || !results?.lcpElement?.selector) return;
    
    setScreenshotLoading(true);
    try {
      const response = await fetch('/api/lab-audits/element-screenshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          url, 
          selector: results.lcpElement.selector,
          viewport: { width: 1280, height: 800 }
        })
      });
      
      const data = await response.json();
      if (data.success && data.screenshot) {
        setScreenshot(data.screenshot);
      }
    } catch (err) {
      console.error('Screenshot error:', err);
    } finally {
      setScreenshotLoading(false);
    }
  };

  const resetResults = () => {
    setResults(null);
    setError(null);
    setMultiPageResults(null);
    setScreenshot(null);
  };

  const formatMs = (ms) => {
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${Math.round(ms)}ms`;
  };

  const getLCPStatus = (lcp) => {
    if (lcp <= 2500) return { status: 'good', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (lcp <= 4000) return { status: 'needs-improvement', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    return { status: 'poor', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const renderPhaseBar = () => {
    if (!results?.phases) return null;

    const { ttfb, resourceLoad, renderDelay } = results.phases;
    // null = undetermined phase (evidence-based decomposition). Without all
    // three known phases, bars/percentages would fabricate a complete
    // breakdown — show an unavailable state instead.
    const known = (v) => v != null && Number.isFinite(Number(v));
    if (![ttfb, resourceLoad, renderDelay].every(known)) {
      return (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-400">
            {language === 'cs' ? 'Fázový breakdown' : 'Phase Breakdown'}
          </div>
          <div className="text-xs text-slate-400">
            {language === 'cs'
              ? 'Rozpad fází nelze z dostupných timing dat spolehlivě určit (např. LCP zdroj bez použitelného load timingu). Report proto žádnou fázi neodhaduje.'
              : 'The phase breakdown cannot be reliably determined from the available timing data (e.g. an LCP resource without usable load timing). No phase is estimated.'}
          </div>
          <div className="flex gap-4 text-xs">
            {[['TTFB', ttfb], [language === 'cs' ? 'Načítání' : 'Load', resourceLoad], ['Render', renderDelay]].map(([label, v]) => (
              <div key={label} className="flex items-center gap-1">
                <span className="text-slate-400">{label}: </span>
                <span className="text-white">{known(v) ? formatMs(v) : (language === 'cs' ? 'nelze určit' : 'undetermined')}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    const total = results.lcp || (ttfb + resourceLoad + renderDelay);

    const ttfbPercent = (ttfb / total) * 100;
    const resourcePercent = (resourceLoad / total) * 100;
    const renderPercent = (renderDelay / total) * 100;

    return (
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-slate-400">
          {language === 'cs' ? 'Fázový breakdown' : 'Phase Breakdown'}
        </div>
        <div className="h-4 flex rounded-lg overflow-hidden">
          <div 
            className="bg-blue-500 flex items-center justify-center"
            style={{ width: `${Math.max(ttfbPercent, 5)}%` }}
            title={`TTFB: ${formatMs(ttfb)}`}
          />
          <div 
            className="bg-purple-500 flex items-center justify-center"
            style={{ width: `${Math.max(resourcePercent, 5)}%` }}
            title={`Resource Load: ${formatMs(resourceLoad)}`}
          />
          <div 
            className="bg-pink-500 flex items-center justify-center"
            style={{ width: `${Math.max(renderPercent, 5)}%` }}
            title={`Render Delay: ${formatMs(renderDelay)}`}
          />
        </div>
        <div className="flex justify-between text-xs">
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-slate-400">TTFB: </span>
            <span className="text-white">{formatMs(ttfb)}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="text-slate-400">{language === 'cs' ? 'Načítání: ' : 'Load: '}</span>
            <span className="text-white">{formatMs(resourceLoad)}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-pink-500" />
            <span className="text-slate-400">{language === 'cs' ? 'Render: ' : 'Render: '}</span>
            <span className="text-white">{formatMs(renderDelay)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderLCPElement = () => {
    if (!results?.lcpElement) return null;
    
    const el = results.lcpElement;
    return (
      <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-cyan-400">
            {language === 'cs' ? 'LCP Element' : 'LCP Element'}
          </div>
          {el.selector && !screenshot && (
            <button
              onClick={captureScreenshot}
              disabled={screenshotLoading}
              className="text-xs px-2 py-1 rounded bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400 flex items-center gap-1 transition-colors disabled:opacity-50"
            >
              {screenshotLoading ? (
                <Icons.Loader size={12} className="animate-spin" />
              ) : (
                <Icons.Camera size={12} />
              )}
              {language === 'cs' ? 'Screenshot' : 'Screenshot'}
            </button>
          )}
        </div>
        
        {screenshot && (
          <div className="rounded-lg overflow-hidden border border-cyan-500/30">
            <img 
              src={`data:image/png;base64,${screenshot}`} 
              alt="LCP Element" 
              className="w-full"
            />
          </div>
        )}
        
        <div className="flex items-center gap-2">
          {el.isImage ? (
            <Icons.Image size={16} className="text-purple-400" />
          ) : (
            <Icons.Type size={16} className="text-blue-400" />
          )}
          <code className="text-sm text-white bg-slate-700 px-2 py-1 rounded">
            {el.selector}
          </code>
        </div>
        {el.src && (
          <div className="text-xs text-slate-400 break-all" title={el.src}>
            {el.src}
          </div>
        )}
        <div className="flex gap-4 text-xs">
          <span className="text-slate-400">
            {language === 'cs' ? 'Velikost: ' : 'Size: '}
            <span className="text-white">{el.rect?.width?.toFixed(0)}x{el.rect?.height?.toFixed(0)}px</span>
          </span>
          {el.hasLazyLoading && (
            <span className="text-red-400 flex items-center gap-1">
              <Icons.AlertTriangle size={12} />
              loading="lazy"
            </span>
          )}
        </div>
      </div>
    );
  };

  const lcpCauseStyles = {
    'slow-server': { icon: Icons.Server, color: 'text-red-400', bg: 'from-red-900/30 to-orange-900/30', border: 'border-red-500/30' },
    'redirect-chain': { icon: Icons.ArrowRight, color: 'text-amber-400', bg: 'from-amber-900/30 to-yellow-900/30', border: 'border-amber-500/30' },
    'render-blocking-css': { icon: Icons.Code, color: 'text-blue-400', bg: 'from-blue-900/30 to-cyan-900/30', border: 'border-blue-500/30' },
    'render-blocking-js': { icon: Icons.FileText, color: 'text-purple-400', bg: 'from-purple-900/30 to-indigo-900/30', border: 'border-purple-500/30' },
    'third-party-blocking': { icon: Icons.Globe, color: 'text-orange-400', bg: 'from-orange-900/30 to-amber-900/30', border: 'border-orange-500/30' },
    'lazy-above-fold': { icon: Icons.Image, color: 'text-red-400', bg: 'from-red-900/30 to-rose-900/30', border: 'border-red-500/30' },
    'large-image': { icon: Icons.Image, color: 'text-amber-400', bg: 'from-amber-900/30 to-orange-900/30', border: 'border-amber-500/30' },
    'slow-resource': { icon: Icons.Clock, color: 'text-pink-400', bg: 'from-pink-900/30 to-rose-900/30', border: 'border-pink-500/30' },
    'webfont-blocking': { icon: Icons.Type, color: 'text-cyan-400', bg: 'from-cyan-900/30 to-blue-900/30', border: 'border-cyan-500/30' },
    'render-delay': { icon: Icons.Zap, color: 'text-yellow-400', bg: 'from-yellow-900/30 to-amber-900/30', border: 'border-yellow-500/30' }
  };

  const lcpCauseDescriptions = {
    'slow-server': {
      cs: 'Server odpovídá příliš pomalu (TTFB). Prohlížeč nemůže začít vykreslovat, dokud nedostane první bajty HTML.',
      en: 'Server responds too slowly (TTFB). The browser cannot start rendering until it receives the first bytes of HTML.'
    },
    'redirect-chain': {
      cs: 'Řetěz přesměrování přidává zbytečné síťové roundtripy před načtením stránky.',
      en: 'Redirect chain adds unnecessary network round-trips before the page loads.'
    },
    'render-blocking-css': {
      cs: 'CSS soubory v <head> blokují vykreslení stránky — prohlížeč čeká na stažení a zpracování všech CSS.',
      en: 'CSS files in <head> block page rendering — browser waits for all CSS to download and parse.'
    },
    'render-blocking-js': {
      cs: 'Synchronní JavaScript skripty v <head> blokují parsing HTML a tím zpomalují první vykreslení.',
      en: 'Synchronous JavaScript in <head> blocks HTML parsing, delaying first render.'
    },
    'third-party-blocking': {
      cs: 'Externí zdroje (analytics, widgety, fonty) blokují vykreslení — prohlížeč čeká na vzdálené servery.',
      en: 'External resources (analytics, widgets, fonts) block rendering — browser waits for remote servers.'
    },
    'lazy-above-fold': {
      cs: 'LCP obrázek má loading="lazy", ale je viditelný hned — prohlížeč ho zbytečně odkládá.',
      en: 'LCP image has loading="lazy" but is visible immediately — browser unnecessarily defers it.'
    },
    'large-image': {
      cs: 'LCP obrázek je příliš velký — trvá dlouho ho stáhnout, zvlášť na pomalejších připojeních.',
      en: 'LCP image is too large — takes long to download, especially on slower connections.'
    },
    'slow-resource': {
      cs: 'LCP zdroj (obrázek, font) se načítá příliš pomalu — může být vzdálený server nebo chybějící CDN.',
      en: 'LCP resource (image, font) loads too slowly — may be distant server or missing CDN.'
    },
    'webfont-blocking': {
      cs: 'Webfonty bez font-display:swap blokují zobrazení textu — LCP text element není viditelný, dokud se font nenačte.',
      en: 'Web fonts without font-display:swap block text display — LCP text element is invisible until font loads.'
    },
    'render-delay': {
      cs: 'Po načtení LCP zdroje trvá příliš dlouho, než ho prohlížeč vykreslí — JavaScript nebo layout blokují render.',
      en: 'After LCP resource loads, it takes too long for browser to render it — JavaScript or layout blocking render.'
    }
  };

  const lcpCauseRecommendations = {
    'slow-server': {
      cs: ['Zapněte server-side caching (Redis, Varnish)', 'Použijte CDN pro geografickou distribuci', 'Optimalizujte databázové dotazy'],
      en: ['Enable server-side caching (Redis, Varnish)', 'Use a CDN for geographic distribution', 'Optimize database queries']
    },
    'redirect-chain': {
      cs: ['Aktualizujte interní odkazy na finální URL', 'Spojte více přesměrování do jednoho', 'Použijte HSTS preload pro HTTPS'],
      en: ['Update internal links to final URLs', 'Combine multiple redirects into one', 'Use HSTS preload for HTTPS']
    },
    'render-blocking-css': {
      cs: ['Vložte kritické CSS přímo do <head> (inline)', 'Načítejte nekritické CSS asynchronně', 'Použijte <link rel="preload"> pro kritické CSS'],
      en: ['Inline critical CSS in <head>', 'Load non-critical CSS asynchronously', 'Use <link rel="preload"> for critical CSS']
    },
    'render-blocking-js': {
      cs: ['Přidejte defer nebo async atribut ke script tagům', 'Přesuňte skripty na konec <body>', 'Použijte dynamický import() pro nekritické moduly'],
      en: ['Add defer or async attribute to script tags', 'Move scripts to end of <body>', 'Use dynamic import() for non-critical modules']
    },
    'third-party-blocking': {
      cs: ['Self-hostujte kritické third-party zdroje', 'Použijte <link rel="preconnect"> pro externí domény', 'Načítejte third-party skripty s async/defer'],
      en: ['Self-host critical third-party resources', 'Use <link rel="preconnect"> for external origins', 'Load third-party scripts with async/defer']
    },
    'lazy-above-fold': {
      cs: ['Odstraňte loading="lazy" z LCP obrázku', 'Přidejte fetchpriority="high"', 'Použijte <link rel="preload" as="image"> v <head>'],
      en: ['Remove loading="lazy" from LCP image', 'Add fetchpriority="high"', 'Use <link rel="preload" as="image"> in <head>']
    },
    'large-image': {
      cs: ['Převeďte na moderní formáty (WebP, AVIF)', 'Změňte rozměry na skutečné zobrazení', 'Použijte responsivní obrázky se srcset'],
      en: ['Convert to modern formats (WebP, AVIF)', 'Resize to actual display dimensions', 'Use responsive images with srcset']
    },
    'slow-resource': {
      cs: ['Použijte <link rel="preload"> pro LCP obrázek', 'Servírujte z CDN blízko uživatelům', 'Zapněte HTTP/2 nebo HTTP/3'],
      en: ['Use <link rel="preload"> for LCP image', 'Serve from CDN close to users', 'Enable HTTP/2 or HTTP/3']
    },
    'webfont-blocking': {
      cs: ['Přidejte font-display: swap do @font-face', 'Preloadujte kritické fonty', 'Použijte system font stack jako fallback'],
      en: ['Add font-display: swap to @font-face', 'Preload critical fonts', 'Use system font stack as fallback']
    },
    'render-delay': {
      cs: ['Minimalizujte práci main threadu před LCP', 'Snižte dobu JavaScriptu', 'Použijte CSS containment kde je to vhodné'],
      en: ['Minimize main thread work before LCP', 'Reduce JavaScript execution time', 'Use CSS containment where appropriate']
    }
  };

  const lcpCauseExamples = {
    'render-blocking-css': {
      bad: '<link rel="stylesheet" href="styles.css">\n<link rel="stylesheet" href="theme.css">\n<link rel="stylesheet" href="utils.css">',
      good: '<style>/* critical CSS inline */</style>\n<link rel="preload" href="styles.css" as="style"\n  onload="this.rel=\'stylesheet\'">'
    },
    'render-blocking-js': {
      bad: '<head>\n  <script src="analytics.js"></script>\n  <script src="app.js"></script>\n</head>',
      good: '<head>\n  <script src="analytics.js" defer></script>\n  <script src="app.js" defer></script>\n</head>'
    },
    'lazy-above-fold': {
      bad: '<img src="hero.jpg" loading="lazy" alt="Hero">',
      good: '<img src="hero.jpg" fetchpriority="high" alt="Hero">\n<!-- + in <head>: -->\n<link rel="preload" as="image" href="hero.jpg">'
    },
    'large-image': {
      bad: '<img src="photo.png" width="1200" height="800">\n<!-- 1.2 MB PNG -->',
      good: '<img src="photo.webp" width="1200" height="800"\n  srcset="photo-600.webp 600w, photo-1200.webp 1200w"\n  sizes="(max-width: 600px) 600px, 1200px">'
    },
    'webfont-blocking': {
      bad: '@font-face {\n  font-family: "MyFont";\n  src: url(font.woff2);\n}',
      good: '@font-face {\n  font-family: "MyFont";\n  src: url(font.woff2);\n  font-display: swap;\n}\n<!-- + preload in <head> -->\n<link rel="preload" as="font" href="font.woff2"\n  type="font/woff2" crossorigin>'
    },
    'third-party-blocking': {
      bad: '<link rel="stylesheet"\n  href="https://fonts.googleapis.com/css?family=Roboto">',
      good: '<link rel="preconnect"\n  href="https://fonts.googleapis.com">\n<link rel="preconnect"\n  href="https://fonts.gstatic.com" crossorigin>'
    },
    'slow-server': {
      bad: 'HTTP/1.1 200 OK\nX-Response-Time: 1800ms\n<!-- No caching headers -->',
      good: 'HTTP/2 200 OK\nX-Response-Time: 180ms\nCache-Control: public, max-age=3600\nCDN-Cache-Control: max-age=86400'
    }
  };

  const renderCausesAndRecommendations = () => {
    if (!results?.causes?.length) return null;

    return (
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-wider text-orange-400 flex items-center gap-1">
          {Icons?.Target ? <Icons.Target size={12} /> : <span>🎯</span>}
          {language === 'cs' ? 'Příčiny a doporučení pro vývoj' : 'Causes & Development Recommendations'}
        </div>
        {results.causes.map((cause, i) => {
          const style = lcpCauseStyles[cause.cause] || { icon: Icons.AlertTriangle, color: 'text-slate-400', bg: 'from-slate-800/50 to-slate-700/30', border: 'border-slate-600' };
          const IconComponent = style.icon || Icons.AlertTriangle;

          return (
            <div key={i} className={`p-4 rounded-lg bg-gradient-to-r ${style.bg} border ${style.border}`}>
              <div className={`text-sm font-medium ${style.color} mb-1 flex items-center gap-2`}>
                <IconComponent size={14} />
                {causeLabels[cause.cause]?.[language] || cause.cause}
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  cause.severity === 'critical' ? 'bg-red-500/30 text-red-300' :
                  cause.severity === 'high' ? 'bg-red-500/30 text-red-300' : 
                  cause.severity === 'medium' ? 'bg-amber-500/30 text-amber-300' : 
                  'bg-slate-500/30 text-slate-300'
                }`}>
                  {cause.severity === 'critical' ? (language === 'cs' ? 'kritická' : 'critical') :
                   cause.severity === 'high' ? (language === 'cs' ? 'vysoká' : 'high') :
                   cause.severity === 'medium' ? (language === 'cs' ? 'střední' : 'medium') :
                   (language === 'cs' ? 'nízká' : 'low')}
                </span>
              </div>
              <p className="text-xs text-slate-300 mb-2">
                {lcpCauseDescriptions[cause.cause]?.[language] || cause.description}
              </p>
              <p className="text-[11px] text-slate-400 mb-2 italic">
                {cause.description}
              </p>
              {cause.resources?.length > 0 && (
                <div className="mb-2 space-y-1">
                  {cause.resources.map((res, j) => (
                    <code key={j} className="block text-xs text-slate-400 bg-slate-900/50 px-2 py-1 rounded break-all" title={typeof res === 'string' ? res : res.url || ''}>
                      {typeof res === 'string' ? res : res.url || JSON.stringify(res)}
                    </code>
                  ))}
                </div>
              )}
              {cause.fonts?.length > 0 && (
                <div className="mb-2 space-y-1">
                  {cause.fonts.map((font, j) => (
                    <code key={j} className="block text-xs text-cyan-400 bg-slate-900/50 px-2 py-1 rounded">
                      {font.fontFamily} (font-display: {font.fontDisplay})
                    </code>
                  ))}
                </div>
              )}
              {lcpCauseRecommendations[cause.cause] && (
                <div className="text-xs text-slate-400">
                  <span className="font-medium">{language === 'cs' ? 'Doporučení:' : 'Fix:'}</span>
                  <ul className="list-disc list-inside mt-1 space-y-0.5 ml-2">
                    {lcpCauseRecommendations[cause.cause][language]?.map((rec, j) => (
                      <li key={j}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
              {lcpCauseExamples[cause.cause] && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs font-medium text-slate-400">
                    {language === 'cs' ? 'Jak opravit:' : 'How to fix:'}
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="p-2 rounded bg-red-900/20 border border-red-500/20">
                      <div className="text-[10px] text-red-400 mb-1 flex items-center gap-1">
                        <Icons.X size={10} /> {language === 'cs' ? 'Problémový kód' : 'Problem code'}
                      </div>
                      <pre className="text-[11px] text-red-300 whitespace-pre-wrap break-all">
                        {lcpCauseExamples[cause.cause].bad}
                      </pre>
                    </div>
                    <div className="p-2 rounded bg-green-900/20 border border-green-500/20">
                      <div className="text-[10px] text-green-400 mb-1 flex items-center gap-1">
                        <Icons.Check size={10} /> {language === 'cs' ? 'Opravený kód' : 'Fixed code'}
                      </div>
                      <pre className="text-[11px] text-green-300 whitespace-pre-wrap break-all">
                        {lcpCauseExamples[cause.cause].good}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {results.recommendations?.length > results.causes?.length && (
          <div className="space-y-2 mt-3">
            <div className="text-xs uppercase tracking-wider text-green-400 flex items-center gap-1">
              <Icons.Lightbulb size={12} />
              {language === 'cs' ? 'Další doporučení' : 'Additional Recommendations'}
            </div>
            {results.recommendations.slice(results.causes.length).map((rec, i) => (
              <div key={i} className="p-3 rounded-lg bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/30">
                <div className="flex items-start gap-2">
                  <Icons.Lightbulb size={14} className="text-green-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-white font-medium">{rec.title}</span>
                    <p className="text-xs text-slate-400 mt-1">{rec.description}</p>
                    {rec.fix && (
                      <pre className="mt-2 text-[11px] text-cyan-300 bg-slate-900/50 p-2 rounded overflow-x-auto whitespace-pre-wrap">
                        {rec.fix}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMultiPageResults = () => {
    if (!multiPageResults) return null;
    
    const { pages, summary } = multiPageResults;
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-indigo-400 flex items-center gap-1">
            <Icons.Globe size={12} /> {language === 'cs' ? 'Multi-page LCP analýza' : 'Multi-Page LCP Analysis'}
          </div>
          <button 
            onClick={() => { setMultiPageResults(null); onResultsChange?.({ multiPageLCP: null }); }}
            className="text-xs text-slate-400 hover:text-white transition-colors"
            title={language === 'cs' ? 'Zavřít' : 'Close'}
          >
            <Icons.X size={14} />
          </button>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2 rounded bg-green-900/30 border border-green-500/30 text-center">
            <div className="text-lg font-bold text-green-400">{summary.goodPages}</div>
            <div className="text-xs text-green-300">{language === 'cs' ? 'Dobré' : 'Good'}</div>
          </div>
          <div className="p-2 rounded bg-amber-900/30 border border-amber-500/30 text-center">
            <div className="text-lg font-bold text-amber-400">{summary.needsImprovementPages}</div>
            <div className="text-xs text-amber-300">{language === 'cs' ? 'K zlepšení' : 'Needs work'}</div>
          </div>
          <div className="p-2 rounded bg-red-900/30 border border-red-500/30 text-center">
            <div className="text-lg font-bold text-red-400">{summary.poorPages}</div>
            <div className="text-xs text-red-300">{language === 'cs' ? 'Špatné' : 'Poor'}</div>
          </div>
        </div>
        
        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
          <div className="flex justify-between text-xs text-slate-400 mb-2">
            <span>
              {language === 'cs' ? 'Průměrné LCP:' : 'Average LCP:'} 
              <span className={`ml-1 font-bold ${summary.avgLCP <= 2500 ? 'text-green-400' : summary.avgLCP <= 4000 ? 'text-amber-400' : 'text-red-400'}`}>
                {formatMs(summary.avgLCP)}
              </span>
            </span>
            <span>
              {language === 'cs' ? 'Rozsah:' : 'Range:'} 
              <span className="ml-1 text-white">{formatMs(summary.minLCP)} - {formatMs(summary.maxLCP)}</span>
            </span>
          </div>
          
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {pages.sort((a, b) => (b.lcp || 0) - (a.lcp || 0)).map((page, i) => {
              const pathMatch = page.url.match(/https?:\/\/[^/]+(.*)/);
              const path = pathMatch ? pathMatch[1] || '/' : page.url;
              const isExpanded = expandedPageIndex === i;
              const hasCauses = page.causes && page.causes.length > 0;
              
              return (
                <div key={i} className="rounded bg-slate-900/50 overflow-hidden">
                  <div 
                    className={`flex items-center justify-between p-2 ${hasCauses ? 'cursor-pointer hover:bg-slate-800/50' : ''}`}
                    onClick={() => hasCauses && setExpandedPageIndex(isExpanded ? null : i)}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {hasCauses && (
                        <Icons.ChevronRight 
                          size={12} 
                          className={`text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      )}
                      <span className="text-xs text-slate-300 break-all">{path}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-mono ${
                        page.status === 'good' ? 'text-green-400' : 
                        page.status === 'needs-improvement' ? 'text-amber-400' : 
                        page.status === 'error' ? 'text-slate-500' : 'text-red-400'
                      }`}>
                        {page.error ? 'Error' : formatMs(page.lcp)}
                      </span>
                      <div className={`w-2 h-2 rounded-full ${
                        page.status === 'good' ? 'bg-green-500' : 
                        page.status === 'needs-improvement' ? 'bg-amber-500' : 
                        page.status === 'error' ? 'bg-slate-500' : 'bg-red-500'
                      }`} />
                    </div>
                  </div>
                  
                  {isExpanded && hasCauses && (
                    <div className="px-3 pb-2 pt-1 border-t border-slate-700/50">
                      <div className="text-xs text-slate-400 mb-1">
                        {language === 'cs' ? 'Příčiny:' : 'Causes:'}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {page.causes.map((cause, j) => (
                          <span key={j} className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
                            {causeLabels[cause.type]?.[language] || cause.type}
                          </span>
                        ))}
                      </div>
                      {page.lcpElement && (
                        <div className="mt-2 text-xs text-slate-400">
                          LCP: <code className="text-cyan-400 bg-slate-800 px-1 rounded">{page.lcpElement.selector}</code>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {summary.worstPage && (
          <div className="p-2 rounded-lg bg-red-900/20 border border-red-500/30">
            <div className="text-xs text-red-400">
              {language === 'cs' ? 'Nejhorší stránka:' : 'Worst page:'} 
              <span className="ml-1 text-white">{summary.worstPage.url}</span>
              <span className="ml-2 font-bold">{formatMs(summary.worstPage.lcp)}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!user) {
    return (
      <div className="text-center py-4 text-slate-400 text-sm">
        {language === 'cs' ? 'Pro LCP diagnostiku se přihlaste' : 'Sign in for LCP diagnostics'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!results ? (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex items-start gap-3">
              <Icons.Zap size={20} className="text-purple-400 mt-0.5" />
              <div>
                <div className="text-sm text-white font-medium">
                  {language === 'cs' ? 'Hloubková LCP analýza' : 'Deep LCP Analysis'}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  {language === 'cs' 
                    ? 'Analyzuje LCP element, fáze načítání (TTFB, resource load, render delay), blokující zdroje a generuje konkrétní doporučení.'
                    : 'Analyzes LCP element, loading phases (TTFB, resource load, render delay), blocking resources and generates specific recommendations.'}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex-1">
              <div className="text-sm text-white font-medium">
                {language === 'cs' ? 'Přeskočit cookie banner' : 'Skip cookie banner'}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {language === 'cs' 
                  ? 'Pokusí se automaticky akceptovat běžné CMP (Didomi, Cookiebot, OneTrust)'
                  : 'Attempts to auto-accept common CMPs (Didomi, Cookiebot, OneTrust)'}
              </div>
            </div>
            <button
              onClick={() => setSkipCmp(!skipCmp)}
              className={`relative w-12 h-6 rounded-full transition-colors ${skipCmp ? 'bg-purple-600' : 'bg-slate-600'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${skipCmp ? 'left-7' : 'left-1'}`} />
            </button>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <button
            onClick={runDiagnostics}
            disabled={loading}
            className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <>
                <Icons.Loader size={16} className="animate-spin" />
                {language === 'cs' ? 'Analyzuji LCP...' : 'Analyzing LCP...'}
              </>
            ) : (
              <>
                <Icons.Search size={16} />
                {language === 'cs' ? 'Spustit hloubkovou LCP analýzu' : 'Run Deep LCP Analysis'}
                {skipCmp && <span className="text-xs opacity-75">({language === 'cs' ? 'bez CMP' : 'no CMP'})</span>}
              </>
            )}
          </button>
        </div>
      ) : !results.lcp || results.lcp === 0 ? (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm flex items-center gap-2">
            <Icons.AlertTriangle size={16} />
            {language === 'cs' 
              ? 'LCP element nebyl zachycen. Zkuste to znovu nebo zkontrolujte, zda stránka načítá viditelný obsah.'
              : 'LCP element was not captured. Try again or check if the page loads visible content.'}
          </div>
          <button
            onClick={resetResults}
            className="w-full py-2 px-4 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm flex items-center justify-center gap-2"
          >
            <Icons.RefreshCw size={14} />
            {language === 'cs' ? 'Zkusit znovu' : 'Try again'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`px-3 py-1.5 rounded-lg ${getLCPStatus(results.lcp).bg}`}>
                <span className={`text-lg font-bold ${getLCPStatus(results.lcp).color}`}>
                  LCP: {formatMs(results.lcp)}
                </span>
              </div>
              <span className="text-xs text-slate-400">
                {language === 'cs' ? 'Cíl: pod 2.5s' : 'Target: under 2.5s'}
              </span>
              {results.skipCmp && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">
                  {language === 'cs' ? 'bez CMP' : 'no CMP'}
                </span>
              )}
            </div>
            <button
              onClick={resetResults}
              className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
            >
              <Icons.RefreshCw size={12} />
              {language === 'cs' ? 'Znovu' : 'Re-run'}
            </button>
          </div>

          {renderPhaseBar()}
          {renderLCPElement()}
          {renderCausesAndRecommendations()}

          <div className="border-t border-slate-700 pt-4">
            {!multiPageResults ? (
              <button
                onClick={runMultiPageLCP}
                disabled={multiPageLoading}
                className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {multiPageLoading ? (
                  <>
                    <Icons.Loader size={14} className="animate-spin" />
                    {language === 'cs' ? 'Analyzuji více stránek...' : 'Analyzing multiple pages...'}
                  </>
                ) : (
                  <>
                    <Icons.Globe size={14} />
                    {language === 'cs' ? 'Analyzovat LCP na více stránkách' : 'Analyze LCP on multiple pages'}
                  </>
                )}
              </button>
            ) : (
              renderMultiPageResults()
            )}
            <p className="text-xs text-slate-500 mt-2 text-center">
              {language === 'cs' 
                ? 'Analyzuje až 10 stránek webu a porovná jejich LCP hodnoty'
                : 'Analyzes up to 10 pages of the website and compares their LCP values'}
            </p>
          </div>

          <div className="text-xs text-slate-500 text-center pt-2 border-t border-slate-700">
            {language === 'cs' 
              ? `Analýza dokončena za ${(results.duration / 1000).toFixed(1)}s`
              : `Analysis completed in ${(results.duration / 1000).toFixed(1)}s`}
          </div>
        </div>
      )}
      
      {showUpgradeModal && (
        <div className="cv-app-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-slate-900 border-2 border-purple-500/50 rounded-2xl p-6 max-w-md mx-4 shadow-2xl shadow-purple-500/20" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Icons.Zap size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'cs' ? 'Hloubková LCP analýza' : 'Deep LCP Analysis'}
              </h3>
              <p className="text-slate-400 mb-4">
                {language === 'cs' 
                  ? 'Hloubkové analýzy jsou dostupné pouze pro tarify Premium a Enterprise.'
                  : 'Deep analyses are available only for Premium and Enterprise plans.'}
              </p>
              <div className="space-y-2">
                <a href="/pricing" className="block w-full py-3 px-4 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium transition-all">
                  {language === 'cs' ? 'Zobrazit tarify' : 'View Plans'}
                </a>
                <button onClick={() => setShowUpgradeModal(false)} className="w-full py-2 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm transition-all">
                  {language === 'cs' ? 'Zavřít' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LCPDiagnosticsPanel;
